import React, { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import PaymentsIcon from '@mui/icons-material/Payments';
import SaveIcon from '@mui/icons-material/Save';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { SMART_COLORS, smartCardSx } from '../../theme/smartStableTheme';
import { formatDateTimeInput } from '../../utils/dateUtils';

const apiUrl = process.env.REACT_APP_API_URL;

const AddReproductionProcedure = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    procedure_name: '',
    description: '',
    default_price: '',
    is_active: true,
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const userId = localStorage.getItem('userId');
      const payload = {
        ...formData,
        default_price: formData.default_price || 0,
        created_by: userId,
        created_at: formatDateTimeInput(new Date()).replace('T', ' '),
      };

      const response = await axios.post(
        `${apiUrl}/reproduction-clinic/procedures`,
        payload
      );

      if (response.status === 201) {
        navigate('/ReproductionProceduresList');
      }
    } catch (error) {
      console.error('Error adding reproduction procedure:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box className="smart-page-container" sx={{ maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          ...smartCardSx,
          p: { xs: 2.5, md: 3 },
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          alignItems: { xs: 'stretch', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              bgcolor: SMART_COLORS.roseSoft,
              color: SMART_COLORS.rose,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <FavoriteBorderIcon />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, color: SMART_COLORS.text }}>
              Add Reproduction Procedure
            </Typography>
            <Typography sx={{ color: SMART_COLORS.muted }}>
              Create a reusable production-clinic procedure and its accounting default price.
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/ReproductionProceduresList')}
          sx={{ borderColor: SMART_COLORS.line, color: SMART_COLORS.text, fontWeight: 800 }}
        >
          Back
        </Button>
      </Paper>

      <Paper elevation={0} sx={{ ...smartCardSx, p: { xs: 2.5, md: 3.5 } }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2.5}>
            <TextField
              fullWidth
              required
              label="Procedure name"
              name="procedure_name"
              value={formData.procedure_name}
              onChange={handleChange}
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={4}
            />
            <TextField
              fullWidth
              label="Default price"
              name="default_price"
              type="number"
              value={formData.default_price}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PaymentsIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: <InputAdornment position="end">SAR</InputAdornment>,
              }}
            />
            <Divider />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => navigate('/ReproductionProceduresList')}
                sx={{ borderColor: SMART_COLORS.line, color: SMART_COLORS.text, fontWeight: 800 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                startIcon={<SaveIcon />}
                sx={{ bgcolor: SMART_COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: SMART_COLORS.goldHover } }}
              >
                Save procedure
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default AddReproductionProcedure;
